import type { ElementDefinition } from 'shared/elements-definition';

import basicHtml from './stories/basic.story.html?raw';
import jsonConfigHtml from './stories/json-config.story.html?raw';

/**
 * Docs definition for the Editor component and its custom element.
 *
 * Used by the docs site to render:
 * - API table for `<e-editor>`
 * - Example stories in the code playground
 */
export const definition: ElementDefinition = {
  componentName: 'Editor',
  description: 'A Shadow-DOM-safe Monaco code editor web component with CDN-loaded assets.',
  elements: [
    {
      tag: 'e-editor',
      description: 'Embeddable Monaco editor with Shadow DOM-safe styling and CDN loading.',
      props: [
        {
          name: 'value',
          type: 'string',
          descriptionKey: 'editor.e-editor.value.description'
        },
        {
          name: 'language',
          type: 'string',
          descriptionKey: 'editor.e-editor.language.description'
        },
        {
          name: 'monaco-theme',
          type: 'string',
          descriptionKey: 'editor.e-editor.monaco-theme.description'
        },
        {
          name: 'theme',
          type: '"light" | "dark"',
          required: true,
          defaultValue: 'dark',
          descriptionKey: 'editor.e-editor.theme.description'
        },
        {
          name: 'read-only',
          type: 'boolean',
          defaultValue: 'false',
          descriptionKey: 'editor.e-editor.read-only.description'
        },
        {
          name: 'minimap',
          type: 'boolean',
          defaultValue: 'false',
          descriptionKey: 'editor.e-editor.minimap.description'
        },
        {
          name: 'automatic-layout',
          type: 'boolean',
          defaultValue: 'false',
          descriptionKey: 'editor.e-editor.automatic-layout.description'
        },
        {
          name: 'auto-detect-high-contrast',
          type: 'boolean',
          defaultValue: 'true',
          descriptionKey: 'editor.e-editor.auto-detect-high-contrast.description'
        },
        {
          name: 'accessibility-help-url',
          type: 'string',
          descriptionKey: 'editor.e-editor.accessibility-help-url.description'
        },
        {
          name: 'aria-container-element',
          type: 'HTMLElement',
          descriptionKey: 'editor.e-editor.aria-container-element.description'
        }
      ],
      styling: [
        {
          name: '--editor-bg',
          kind: 'css-variable',
          description: 'Background color for the editor container.'
        },
        {
          name: '--editor-bg-light',
          kind: 'css-variable',
          description: 'Background color in light theme.'
        },
        {
          name: '--editor-bg-dark',
          kind: 'css-variable',
          description: 'Background color in dark theme.'
        }
      ]
    }
  ],

  stories: [
    {
      id: 'basic',
      name: 'Basic',
      description: 'A basic editor instance with JavaScript content.',
      html: basicHtml
    },
    {
      id: 'json-config',
      name: 'JSON configuration',
      description: 'Editor configured for JSON with a dark theme.',
      html: jsonConfigHtml
    }
  ]
};
